/**
 * Walls from a scan, joined into a room.
 *
 * RoomPlan hands back walls one at a time, in no order, each a segment on the floor with a
 * little noise at the ends: a corner that should be one point is two points 40mm apart, a
 * wall that should be 3600 is 3612. This turns that into the loop the planner wants -- every
 * wall's end touching the next wall's start, clockwise, on whole tens of millimetres -- and
 * says plainly when it cannot.
 *
 * Pure. Tested with jittered boxes and Ls. No Capacitor, no Vue.
 */
import * as room from './room.js'

/** Two ends closer than this are the same corner. Walls are plastered, phones are not lasers. */
const JOIN = 120
/** Nobody's room has a wall shorter than this; it is a door reveal or a scan artefact. */
const MIN_WALL = 200

/**
 * A GAP BETWEEN TWO WALLS THAT LINE UP IS A DOORWAY, up to this wide.
 *
 * RoomPlan does not always report a wide cased opening as an `opening`. Where the plaster
 * stops either side of a 2-metre archway it can report two walls and nothing between them,
 * and this file's only answer to that was "the room did not close" -- so the owner's scan of
 * a kitchen with a giant doorway in it either failed outright or, when the pieces happened to
 * chain the other way round, closed through the wrong corner.
 *
 * Two pieces that point the same way with a hole between them are one wall with a hole in it.
 * The hole is bridged with a wall segment marked `bridged`, and `reproduce.js` turns that
 * mark into a full-height, full-width opening -- so the cabinets stay out of it, the plan
 * draws a break, and the 3D room has a doorway rather than a wall across the entrance.
 */
const MAX_BRIDGE = 4000
/**
 * And no narrower than this, or it is a scan that fell short rather than a doorway.
 *
 * Nobody hangs a door under 700, and a scan that stops half a metre from a corner is the
 * ordinary way a room fails to close -- somebody turned round too early. Reading that as a
 * doorway would close the room over the top of a real fault and put a hole in a wall that
 * has none.
 */
const MIN_BRIDGE = 700
/** How nearly parallel two pieces have to be before they are one wall. About twenty degrees. */
const BRIDGE_DOT = Math.cos(20 * Math.PI / 180)

/**
 * SMALLER THAN THIS IS NOT A ROOM, it is a sliver that happened to close.
 *
 * Now that a chain is allowed to stop the moment it closes, three pieces that nearly line up
 * can shut on themselves and come back as a "room" a hand's breadth across. Nobody's kitchen
 * is a square metre, and a candidate that small is worth less than the refusal it would
 * replace -- so it is not a candidate at all.
 */
const MIN_AREA = 1e6

/**
 * How many chains we are willing to try starting from.
 *
 * Trying every start is one chain per segment per orientation, each chain O(N) steps that
 * each scan O(N) segments. At the size a room scan actually is that is nothing: a kitchen is
 * a dozen wall pieces, and a whole open-plan floor with stubs, returns and mirror splits is
 * perhaps forty. A scan that comes back with several hundred pieces is not a room, and this
 * cap is here so that one cannot make somebody's phone think for a minute -- past this many,
 * only the LONGEST pieces are tried as starts, because a real wall of a real room is long and
 * the chaff a bad scan leaves behind is short.
 */
const MAX_STARTS = 120

/**
 * How far off the bounding box a wall may sit and still be called the back or the left.
 *
 * A tape's worth rather than a corner's. The room has just been squared up on its longest
 * wall, so the wall opposite is square to a degree or two and its middle can stand a couple
 * of hundred millimetres off the box -- which at the old tolerance meant the front wall of
 * a perfectly ordinary scanned room kept a number and every note about it said "Wall w2".
 */
const NAME_SLACK = 300

const unit = (p, q) => {
  const L = Math.hypot(q.x - p.x, q.y - p.y) || 1
  return { x: (q.x - p.x) / L, y: (q.y - p.y) / L }
}
const dot = (u, v) => u.x * v.x + u.y * v.y

/**
 * The turn that squares a scanned room up: its longest wall along +x, with the room in front.
 *
 * "In front" is the second half and it matters as much as the first. Turning the longest wall
 * onto +x leaves two answers a half-turn apart -- the room above the wall or below it -- and
 * only one of them makes that wall the BACK, which is what every preset, every camera angle
 * and every note in this app means by the word. So the loop's own middle decides: the back
 * wall is the one with the room on the far side of it from the viewer, which on a plan drawn
 * with y downward means the wall is at the smaller y.
 *
 * @param {Array<{x, y}>} corners  the loop, in the scan's own frame
 * @returns {number} radians, to be applied as a rotation about the scan's origin
 */
function frameAngle(corners) {
  const n = corners.length
  if (n < 3) return 0
  let best = null
  for (let i = 0; i < n; i++) {
    const p = corners[i], q = corners[(i + 1) % n]
    const len = Math.hypot(q.x - p.x, q.y - p.y)
    if (!best || len > best.len + 0.5) best = { len, p, q }
  }
  let a = -Math.atan2(best.q.y - best.p.y, best.q.x - best.p.x)
  const turn = (p) => ({ x: p.x * Math.cos(a) - p.y * Math.sin(a), y: p.x * Math.sin(a) + p.y * Math.cos(a) })
  const mid = turn({ x: (best.p.x + best.q.x) / 2, y: (best.p.y + best.q.y) / 2 })
  const mean = corners.map(turn).reduce((s, c) => s + c.y, 0) / n
  if (mid.y > mean) a += Math.PI
  return a
}

/**
 * A scanned POINT in the planner's frame: turned by the room's angle, then moved with it.
 *
 * @param {{x, y, angle}|null} frame  what `loopFromSegments` returned as `origin`
 */
export function toPlan(frame, x, z) {
  const a = frame?.angle ?? 0
  const c = Math.cos(a), s = Math.sin(a)
  return { x: x * c - z * s - (frame?.x ?? 0), y: x * s + z * c - (frame?.y ?? 0) }
}

/** A scanned DIRECTION in the planner's frame: turned with the room, never moved. */
export function dirToPlan(frame, dx, dz) {
  const a = frame?.angle ?? 0
  const c = Math.cos(a), s = Math.sin(a)
  return { x: dx * c - dz * s, y: dx * s + dz * c }
}

/**
 * Is the space between these two pieces a hole in a wall, rather than a missing wall?
 *
 * A doorway lies ALONG the wall it is cut into, and that wall is either the piece before the
 * hole or the piece after it -- an archway in the middle of a run of plaster has one on both
 * sides, and an archway hard against a corner has one only on the far side. So the gap has to
 * run in the same direction as one of its two neighbours.
 *
 * The test is what keeps this from papering over a real fault. A scan that missed a whole
 * wall leaves a gap that runs ACROSS both neighbours, and bridging that would draw a wall
 * through the middle of somebody's kitchen and call the room closed -- which is a worse
 * answer than the refusal it would have replaced.
 */
function linesUp(tailA, tailB, near, far) {
  const bridge = unit(tailB, near)
  return dot(bridge, unit(tailA, tailB)) >= BRIDGE_DOT || dot(bridge, unit(near, far)) >= BRIDGE_DOT
}

/**
 * The corners of a chain: each join averaged from the two ends that meet there.
 *
 * Pulled out of the body below because the loop's corners are now needed BEFORE a loop is
 * chosen -- its area and what it encloses are how the choice is made -- as well as after.
 */
const cornersOf = (chain) => chain.map((s, i) => {
  const prev = chain[(i - 1 + chain.length) % chain.length]
  return { x: (s.a.x + prev.b.x) / 2, y: (s.a.y + prev.b.y) / 2 }
})

/** Unsigned shoelace: how much floor the loop encloses, whichever way round it was walked. */
const areaOf = (cs) => Math.abs(cs.reduce((a, c, i) => {
  const d = cs[(i + 1) % cs.length]
  return a + (c.x * d.y - d.x * c.y)
}, 0) / 2)

/** Ray casting: is this point inside the loop? Used to ask which loop the kitchen is in. */
function encloses(cs, p) {
  let hit = false
  for (let i = 0, j = cs.length - 1; i < cs.length; j = i++) {
    const a = cs[i], b = cs[j]
    if ((a.y > p.y) !== (b.y > p.y) && p.x < (b.x - a.x) * (p.y - a.y) / (b.y - a.y) + a.x) hit = !hit
  }
  return hit
}

/**
 * A name for the SHAPE of a loop, independent of where the chain started or which way it went.
 *
 * Two chains that walk the same room from different segments, or round it the other way, are
 * the same room and must not be counted as two candidates -- and where two genuinely different
 * rooms tie on every measure that matters, this is what stops the answer depending on the
 * order the phone happened to report the walls in.
 */
const keyOf = (cs) => cs.map((c) => `${Math.round(c.x / 10)},${Math.round(c.y / 10)}`).sort().join(' ')

/**
 * The best closed loop in a pile of scanned wall pieces, and what it left out.
 *
 * THIS USED TO BE ALL OR NOTHING, and that is the bug it exists to fix. It demanded that
 * every single segment chain into one closed loop, so one piece that could not be chained --
 * a stub, a peninsula return, a wall of the next room, a run of plaster the LiDAR split in
 * two across a mirror -- failed the whole scan, and the page threw the scan away and put the
 * owner back on the Start card with the demo kitchen still on screen. Their words: "It
 * scanned but then wouldn't let me progress."
 *
 * An open-plan scan can essentially never satisfy that rule. Somebody scanning their kitchen
 * walks through the archway into the living room, because that is where you stand to see the
 * far end of the island, and the scan comes back with the kitchen's walls AND the living
 * area's -- two rooms' worth of geometry, one of which is the room they asked about.
 *
 * So: chain from every segment in turn, in both directions, stopping each chain the moment it
 * closes on its own start; then CHOOSE among the loops that closed, and say what was left
 * over rather than failing for it. The corner rule and the doorway rule are untouched --
 * widening either would buy closure by accepting worse corners, which is the wrong trade. The
 * only genuine failure left is that nothing closed at all.
 *
 * @param {Array<{cx, cz, dx, dz, length}>} segments  from the plugin, mm and unit vectors
 * @param {{ inside?: Array<{cx, cz}> }} [opts]  points the room ought to contain -- the scan's
 *   own objects, the cupboards and appliances it found. Used ONLY to rank candidate loops:
 *   the loop with the kitchen's things in it is the kitchen. Optional, and absent the ranking
 *   falls through to area, so every existing caller keeps the answer it had.
 * @returns {{ walls: Wall[] | null, problem: string | null, dropped: number,
 *             origin: {x, y, angle} | null, leftOver: number, leftOverLength: number }}
 *   `dropped` means what it has always meant: pieces shorter than `MIN_WALL`, thrown away
 *   before any of this. `leftOver` is how many of the pieces that survived that are not in
 *   the room returned, and `leftOverLength` their total length in millimetres -- the next
 *   room, in the owner's scan. When no room could be made, everything is left over.
 */
export function loopFromSegments(segments, opts = {}) {
  const segs = segments
    .filter((s) => s.length >= MIN_WALL)
    .map((s) => {
      const hx = s.dx * s.length / 2, hz = s.dz * s.length / 2
      return { a: { x: s.cx - hx, y: s.cz - hz }, b: { x: s.cx + hx, y: s.cz + hz }, len: s.length }
    })
  const dropped = segments.length - segs.length
  const allLeft = () => ({ leftOver: segs.length, leftOverLength: Math.round(segs.reduce((t, s) => t + s.len, 0)) })
  if (segs.length < 3) {
    return { walls: null, dropped, origin: null, ...allLeft(),
      problem: 'Fewer than three walls were found. Scan slowly, all the way round.' }
  }

  const dist = (p, q) => Math.hypot(p.x - q.x, p.y - q.y)
  const facing = (i, flip) => {
    const s = segs[i]
    return flip ? { a: s.b, b: s.a, len: s.len } : s
  }

  /**
   * One chain, from one segment, in one direction -- run until it closes or runs out.
   *
   * At each step take the unused segment whose one end is nearest this segment's far end,
   * flipping it if its other end is the near one. The order the four ways of continuing are
   * tried in is the whole behaviour of this function:
   *
   *   1. CLOSED, if the tail has come back to where the chain started. A chain stops here.
   *      It used to keep going until every segment was consumed, which is precisely why one
   *      unchainable piece anywhere in the scan destroyed a room that had already closed.
   *   2. A CORNER, if some unused piece begins within `JOIN` of the tail. Always preferred:
   *      a real corner is never passed over in favour of a guess.
   *   3. A DOORWAY BACK ONTO THE START, closing the room through the archway it opens by.
   *      Preferred over 4 because closing the room you are standing in beats reaching into
   *      the next one -- and where that is the wrong call, the larger loop is still found
   *      from some other start and wins on area.
   *   4. A DOORWAY ONWARD. The nearest end is not the right candidate here: the nearest
   *      thing to a wall that stops at an archway is routinely the far end of the wall
   *      behind you, which is closer than the piece the archway actually opens into. So the
   *      bridge is chosen among the pieces the gap LINES UP with -- and if none of them
   *      does, this chain is simply not a room, and some other chain may be.
   */
  function chainFrom(startIdx, startFlip) {
    const used = new Set([startIdx])
    const chain = [facing(startIdx, startFlip)]
    const head = chain[0]
    for (;;) {
      const last = chain[chain.length - 1]
      const tail = last.b
      const home = dist(tail, head.a)
      if (chain.length >= 3 && home <= JOIN) return { closed: true, chain, used }

      let best = null
      segs.forEach((s, i) => {
        if (used.has(i)) return
        const da = dist(tail, s.a), db = dist(tail, s.b)
        const d = Math.min(da, db)
        if (!best || d < best.d) best = { i, d, flip: db < da }
      })
      if (best && best.d <= JOIN) {
        used.add(best.i)
        chain.push(facing(best.i, best.flip))
        continue
      }

      if (chain.length >= 3 && home >= MIN_BRIDGE && home <= MAX_BRIDGE
          && linesUp(last.a, last.b, head.a, head.b)) {
        chain.push({ a: tail, b: head.a, len: home, bridged: true })
        return { closed: true, chain, used }
      }

      let span = null
      segs.forEach((s, i) => {
        if (used.has(i)) return
        for (const flip of [false, true]) {
          const piece = facing(i, flip)
          const d = dist(tail, piece.a)
          if (d > MAX_BRIDGE || d < MIN_BRIDGE) continue
          if (!linesUp(last.a, last.b, piece.a, piece.b)) continue
          if (!span || d < span.d) span = { i, flip, d, piece }
        }
      })
      if (!span) {
        // Where it gave up, in the words the person will read: either a piece that is near
        // enough to be suspicious but too far to be a corner, or nothing left and a room
        // that never shut.
        return { closed: false, chain, used,
          stall: best ? { d: best.d, kind: 'corner' } : { d: home, kind: 'close' } }
      }
      chain.push({ a: tail, b: span.piece.a, len: span.d, bridged: true })
      used.add(span.i)
      chain.push(span.piece)
    }
  }

  /*
   * EVERY START, AND BOTH WAYS ROUND.
   *
   * Which segment a chain begins at decides which room it finds, and the plugin hands the
   * walls back in no particular order, so the first one is not the kitchen's any more often
   * than chance. Direction matters too: the greedy nearest-end rule walking clockwise from a
   * corner and walking anticlockwise from it can pick up different pieces at the first
   * ambiguous junction, which in an open-plan scan is the junction between the two rooms.
   */
  const order = segs.length <= MAX_STARTS
    ? segs.map((_, i) => i)
    : segs.map((_, i) => i).sort((a, b) => segs[b].len - segs[a].len || a - b).slice(0, MAX_STARTS)

  const want = (opts.inside ?? []).map((p) => ({ x: p.cx, y: p.cz }))
  const found = new Map()
  let stalled = null
  for (const i of order) {
    for (const flip of [false, true]) {
      const c = chainFrom(i, flip)
      if (!c.closed) {
        // Kept only to explain a total failure. The longest chain is the most informative
        // one, and where two are equally long the one that ran out of walls says more than
        // the one that merely found a bad corner.
        const better = !stalled || c.used.size > stalled.used.size
          || (c.used.size === stalled.used.size && c.stall.kind === 'close' && stalled.stall.kind !== 'close')
        if (better) stalled = c
        continue
      }
      const corners = cornersOf(c.chain)
      const area = areaOf(corners)
      if (area < MIN_AREA) continue
      const key = keyOf(corners)
      const prev = found.get(key)
      // The same room, walked from somewhere else: keep the lowest starting index, so the
      // chain that represents a given room is always the same chain.
      if (prev && prev.start <= i) continue
      found.set(key, { chain: c.chain, used: c.used, corners, area, key, start: i,
        inside: want.reduce((k, p) => k + (encloses(corners, p) ? 1 : 0), 0) })
    }
  }

  if (!found.size) {
    const joined = stalled?.used.size ?? 0
    const gap = Math.round(stalled?.stall.d ?? 0)
    const what = stalled?.stall.kind === 'close'
      ? `The last wall ends ${gap}mm from where the first begins. The room did not close.`
      : `A wall is ${gap}mm from the next one, which is not a corner.`
    return { walls: null, dropped, origin: null, ...allLeft(),
      problem: `${what} ${segs.length} walls were found and ${joined} of them joined up.`
        + ' Scan the kitchen on its own, all the way round in one go — a scan that takes in the'
        + ' rest of the floor brings back walls that cannot make a room.' }
  }

  /*
   * WHICH OF THE ROOMS IT FOUND IS THE ONE SOMEBODY MEANT.
   *
   * First, always: the loop that holds the most of the scan's own kitchen evidence -- the
   * cupboards, the appliances, the sink, whatever RoomPlan drew a box round. A room with the
   * kitchen's things in it is the kitchen, and this is the only test that can tell a kitchen
   * from a living room of the same size. A loop that shut early on half the kitchen holds
   * fewer of them and loses here, which is what that test is doing as much as the other one.
   *
   * THEN THE SMALLEST SUCH LOOP, WHICH IS THE OPPOSITE OF WHAT IT LOOKS LIKE. In an open-plan
   * scan the kitchen's walls close a room AND the kitchen-plus-living-room's walls close a
   * bigger one around it, and the big one holds every kitchen object the small one does. Both
   * are honest rooms; only one is the room somebody asked to fit out. Taking the largest would
   * hand back the whole floor with a kitchen at one end of it, cupboards drawn along the
   * living room's walls -- so among the loops that hold the same evidence, the TIGHTEST one is
   * taken. It is the room those things are actually in.
   *
   * With no evidence to go on, every loop ties at nothing and the old rule is right again:
   * the largest floor, because then a loop that shut early has nothing to mark it out as the
   * lesser answer except its size. Hence one comparator or the other, chosen once.
   *
   * After that: the most segments used, which separates two readings of the same floor; then
   * the shape itself and the starting index, so the same scan gives the same room every time
   * rather than one that depends on the order the walls arrived in.
   */
  const bySize = want.length ? (a, b) => a.area - b.area : (a, b) => b.area - a.area
  const chosen = [...found.values()].sort((a, b) =>
    b.inside - a.inside || bySize(a, b) || b.chain.length - a.chain.length
    || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0) || a.start - b.start)[0]

  const chain = chosen.chain
  const leftOver = segs.length - chosen.used.size
  const leftOverLength = Math.round(segs.reduce((t, s, i) => t + (chosen.used.has(i) ? 0 : s.len), 0))

  // Snap each corner to the average of the two ends that meet there, then to 10mm, so the
  // loop is exactly closed and every wall is a number a maker would read off a tape.
  const n = chain.length
  const raw = chosen.corners

  /*
   * SQUARE THE ROOM UP, before anything else is measured against it.
   *
   * A scan's frame is the phone's heading at the moment somebody pressed start, so a room
   * comes back at whatever angle they happened to be standing at -- forty degrees, in the
   * owner's kitchen. Everything downstream then reads as skewed: the plan is drawn on the
   * diagonal, `bounds` is the bounding box of a rotated rectangle rather than the room, and
   * no wall is horizontal or vertical enough to be called the back or the left, so every
   * wall keeps a number and every note the app writes says "Wall w2" instead of "the back
   * wall". None of that is a drawing problem; the room is genuinely in the wrong frame.
   *
   * So the loop is turned so its LONGEST wall lies along +x, with the room in front of it.
   * That is the one choice a room can make for itself: the longest wall is the one a kitchen
   * is built along and the one a person draws first. Everything else in the scan -- the
   * cupboards, the appliances, the doors, the windows, the benchtops, the island -- goes
   * through the same turn, which is what `origin.angle` is for.
   */
  const angle = frameAngle(raw)
  const cos = Math.cos(angle), sin = Math.sin(angle)
  const corners = raw.map((c) => ({ x: c.x * cos - c.y * sin, y: c.x * sin + c.y * cos }))

  // Origin at the top-left of the room, so the numbers are small and positive-ish.
  const minX = Math.min(...corners.map((c) => c.x)), minY = Math.min(...corners.map((c) => c.y))
  const snapped = corners.map((c) => ({ x: Math.round((c.x - minX) / 10) * 10, y: Math.round((c.y - minY) / 10) * 10 }))

  let walls = snapped.map((c, i) => {
    const d = snapped[(i + 1) % n]
    return { id: 'w' + i, x1: c.x, y1: c.y, x2: d.x, y2: d.y, ...(chain[i].bridged ? { bridged: true } : {}) }
  })
  walls = room.clockwise(walls)

  // The planner's shape ids, where they are unambiguous: the wall along the top of the plan is
  // the back, the one down the left the left, and so on. Angled walls keep their numbers.
  const bb = room.bounds(walls)
  const named = walls.map((w) => {
    const d = room.dir(w)
    const mid = room.at(w, room.length(w) / 2)
    // Within a corner's worth of the edge counts as on it: a scanned wall is never exactly
    // on the bounding box, its two corners having been snapped separately, and a room that
    // has just been squared up is square to a degree or two rather than to the millimetre.
    const horiz = Math.abs(d.x) > 0.95, vert = Math.abs(d.y) > 0.95
    let id = w.id
    if (horiz && Math.abs(mid.y - bb.minY) < NAME_SLACK) id = 'back'
    else if (horiz && Math.abs(mid.y - bb.maxY) < NAME_SLACK) id = 'front'
    else if (vert && Math.abs(mid.x - bb.minX) < NAME_SLACK) id = 'left'
    else if (vert && Math.abs(mid.x - bb.maxX) < NAME_SLACK) id = 'right'
    return { ...w, id }
  })
  // No two walls may share a name.
  const seen = new Map()
  for (const w of named) {
    const k = seen.get(w.id) ?? 0
    seen.set(w.id, k + 1)
    if (k) w.id = `${w.id}-${k + 1}`
  }
  // Rotate so the back wall comes first, as the presets do.
  const start = Math.max(0, named.findIndex((w) => w.id === 'back'))
  const ordered = [...named.slice(start), ...named.slice(0, start)]
  // The plan's y axis is three.js z: the back wall sits at negative z. Shift so the front
  // wall is at 0 and the back at -depth, matching the presets.
  const depth = bb.maxY - bb.minY
  const shifted = ordered.map((w) => ({ ...w, y1: w.y1 - depth, y2: w.y2 - depth }))
  /*
   * WHERE THE ROOM MOVED TO, so everything else in the scan can follow it.
   *
   * The loop above is TURNED and then re-origined twice: once to put the room's top-left at
   * zero, and once to slide the front wall onto y=0 the way the presets have it. The walls
   * come out in the planner's frame; the cabinets, the doors and the windows in the same
   * scan are still in the phone's. Without this they were laid against the walls of a room
   * that had moved out from under them -- a door on the front wall landed on the back one,
   * or on nothing.
   *
   * `toPlan` and `dirToPlan` below are the transform; use them rather than doing the
   * arithmetic again, because there is exactly one frame and it has three numbers in it.
   * The name `origin` is kept because it is the field the page already passes on.
   */
  return { walls: shifted, problem: null, dropped, leftOver, leftOverLength,
    origin: { x: minX, y: minY + depth, angle } }
}

/**
 * Why the phone in someone's hand cannot scan, in words they can act on.
 *
 * The button used to be drawn only where scanning worked, which is correct and
 * unhelpful: on every other device the feature left no trace at all, so the honest
 * conclusion was that it had never been built. A greyed line that says what is missing
 * is worth more than a clean panel, because "your phone has no LiDAR" and "we never
 * wrote it" are different facts and only one of them is true.
 *
 * @param {{ ok: boolean, reason: string|null, platform: string }} status  from the plugin
 * @returns {string|null} null when it works, otherwise one sentence
 */
export function scanReason(status) {
  if (!status || status.ok) return null
  switch (status.reason) {
    case 'web':
      return 'Scanning needs the app on an iPhone Pro — a browser cannot reach the LiDAR sensor.'
    case 'platform':
      return 'Scanning is an iPhone Pro feature. Type the sizes below instead.'
    case 'hardware':
      return 'This phone has no LiDAR sensor. Scanning needs an iPhone Pro or iPad Pro on iOS 17.'
    default:
      return 'Scanning is not available on this device. Type the sizes below instead.'
  }
}
